// ============================================================================
// Upload Manager -> Manages background upload of offline workers to the backend.
// ============================================================================

import { enqueueSnackbar } from 'notistack';

import { db, updateWorkerUploadStatus, getPendingWorkersByProject, getProcessingWorkersByProject } from './indexedDB';
import {
  StepHandler,
  handleWorkerCreation,
  handlePasswordCreation,
  handleProfilePicture,
  handleGovDocuments,
  handleWorkerUpdate,
  handleEmployers,
  handleCertifications,
  handleSignature,
  handleConsentAcceptances,
  handleCustomConsentAcceptances,
  handleConsentFormData,
  refreshWorkerData,
} from './uploadStepHandlers';
import i18n from 'i18n';

import { IOnboardingState } from 'modules/state-mgmt/onboarding/state';
import { OnboardingStatus } from 'models/general';
import { ROUTES } from 'constants/routes';
import { StepKey } from 'modules/views/shared/Wizard/config';

// ============================================================================
// Module State
// ============================================================================

let autoQueue: string[] = [];
let currentAutoUpload: string | null = null;
let manualUploads: Set<string> = new Set();
let isOnline = true;
let currentProjectId = '';
let currentLanguageCode = 'en';

const MAX_MANUAL_CONCURRENT = 3;

// ============================================================================
// Upload Steps Configuration
// ============================================================================

type UploadStepKey = keyof IOnboardingState['uploadingDetails']['pendingUploads'];

interface UploadStep {
  key: UploadStepKey;
  pageUrl: string;
  handler: StepHandler;
}

/**
 * Get language code from module state (synced from store.app.languageCode).
 */
const getLanguageCode = (): string => {
  return currentLanguageCode || 'en';
};

/**
 * Ordered list of upload steps.
 * Each step has a key, page URL (for error tracking), and handler function.
 */
const UPLOAD_STEPS: UploadStep[] = [
  { key: 'workerCreation', pageUrl: ROUTES.REGISTRATION.path, handler: handleWorkerCreation },
  { key: 'workerPasswordCreation', pageUrl: ROUTES.CREATE_PASSWORD.path, handler: handlePasswordCreation },
  { key: 'employers', pageUrl: ROUTES.REGISTRATION.path, handler: handleEmployers },
  { key: 'signature', pageUrl: ROUTES.REGISTRATION_SIGNATURE.path, handler: handleSignature },
  { key: 'consentAcceptances', pageUrl: ROUTES.REGISTRATION_SIGNATURE.path, handler: handleConsentAcceptances },
  { key: 'profilePicture', pageUrl: ROUTES.TAKE_PHOTO.path, handler: handleProfilePicture },
  { key: 'govDocuments', pageUrl: ROUTES.PERSONAL_INFORMATION.path, handler: handleGovDocuments },
  { key: 'personalInformation', pageUrl: ROUTES.PERSONAL_INFORMATION.path, handler: handleWorkerUpdate },
  { key: 'contactInformation', pageUrl: ROUTES.CONTACT_INFORMATION.path, handler: handleWorkerUpdate },
  { key: 'trades', pageUrl: ROUTES.TRADES.path, handler: handleWorkerUpdate },
  { key: 'certifications', pageUrl: ROUTES.CERTIFICATIONS.path, handler: handleCertifications },
  {
    key: 'customConsentAcceptances',
    pageUrl: ROUTES.CONSENT_FORM.path,
    handler: handleCustomConsentAcceptances,
  },
  { key: 'consentFormData', pageUrl: ROUTES.CONSENT_FORM.path, handler: handleConsentFormData },
];

// ============================================================================
// Backend Error → Page URL Resolution
// ============================================================================

const PERSONAL_INFO_BACKEND_FIELDS = new Set([
  'firstName',
  'middleName',
  'lastName',
  'dateOfBirth',
  'gender',
  'ethnicity',
  'primaryLanguage',
  'veteranStatus',
  'allergies',
  'lastSSN',
  'governmentIDType',
  'governmentIDIssuedBy',
  'governmentIDNumber',
  'governmentIDImage',
  'profilePhoto',
]);

const CONTACT_INFO_BACKEND_FIELDS = new Set([
  'contactEmail',
  'contactMobilePhoneNumber',
  'ContactAlternateMobileNumber',
  'homeAddressLine1',
  'homeAddressLine2',
  'homeAddressCountry',
  'homeAddressStateProvince',
  'homeAddressCity',
  'homeAddressZipPostal',
  'homeAddressCounty',
  'homeAddressBorough',
  'emergencyContactName',
  'emergencyContactPhone',
  'emergencyContactRelationship',
]);

const TRADES_BACKEND_FIELDS = new Set([
  'role',
  'laborUnionStatus',
  'workExperience',
  'laborUnionNumber',
  'trades',
  'otherTradesList',
]);

const PAGE_FIELD_MAPPINGS: { fields: Set<string>; pageUrl: string }[] = [
  { fields: PERSONAL_INFO_BACKEND_FIELDS, pageUrl: ROUTES.PERSONAL_INFORMATION.path },
  { fields: CONTACT_INFO_BACKEND_FIELDS, pageUrl: ROUTES.CONTACT_INFORMATION.path },
  { fields: TRADES_BACKEND_FIELDS, pageUrl: ROUTES.TRADES.path },
];

/**
 * Resolve the correct pageUrl from backend error field names.
 * Checks error keys against known field sets for personalInformation,
 * contactInformation, and trades pages. Returns the first matching page URL.
 */
const getPageUrlFromBackendErrors = (errorObject: Record<string, any> | undefined, fallbackPageUrl: string): string => {
  if (!errorObject || typeof errorObject !== 'object') return fallbackPageUrl;

  const errorKeys = Object.keys(errorObject);
  for (const mapping of PAGE_FIELD_MAPPINGS) {
    if (errorKeys.some(key => mapping.fields.has(key))) {
      return mapping.pageUrl;
    }
  }

  return fallbackPageUrl;
};

const WORKER_UPDATE_STEPS = new Set<UploadStepKey>(['personalInformation', 'contactInformation', 'trades']);

// ============================================================================
// Helper Functions
// ============================================================================

// Function to check if all required and non-skippable or skippable till consent form fields for a step are complete based on sectionsCompleted
const areAllRequiredFieldsComplete = (
  stepKey: UploadStepKey,
  sectionsCompleted: { [key in StepKey]: boolean },
): boolean => {
  switch (stepKey) {
    case 'profilePicture':
      return sectionsCompleted?.[StepKey.TAKE_PHOTO];
    case 'govDocuments':
      return sectionsCompleted?.[StepKey.PERSONAL_INFORMATION];
    case 'personalInformation':
      return sectionsCompleted?.[StepKey.PERSONAL_INFORMATION];
    case 'contactInformation':
      return sectionsCompleted?.[StepKey.CONTACT_INFORMATION];
    case 'trades':
      return sectionsCompleted?.[StepKey.TRADES];
    case 'workerCreation':
    case 'workerPasswordCreation':
    case 'signature':
    case 'consentAcceptances':
    case 'customConsentAcceptances':
    case 'employers':
    case 'certifications':
    case 'consentFormData':
    default:
      return true;
  }
};

/**
 * Core upload logic for a single worker.
 * - On abort (offline): Resets to PENDING
 * - On failure: Stops and marks as INCOMPLETE
 * - On success: Marks as COMPLETED
 */
const executeUpload = async (dbId: string): Promise<boolean> => {
  let worker = await db.workers.get(dbId);
  if (!worker) {
    return false;
  }

  // Set status to PROCESSING and clear any error from a previous attempt.
  await updateWorkerUploadStatus(dbId, {
    status: OnboardingStatus.PROCESSING,
    errors: { pageUrl: '', errorObject: null },
  });
  worker.uploadingDetails.status = OnboardingStatus.PROCESSING;
  worker.uploadingDetails.errors = { pageUrl: '', errorObject: null };

  // Lazy migration: remap legacy 'consentFormSignature' key → 'signature'
  if (
    (worker.uploadingDetails?.pendingUploads as any)?.consentFormSignature &&
    !worker.uploadingDetails?.pendingUploads?.signature
  ) {
    worker.uploadingDetails.pendingUploads.signature = true;
    delete (worker.uploadingDetails.pendingUploads as any).consentFormSignature;
    await db.workers.update(dbId, { uploadingDetails: worker.uploadingDetails });
  }

  // Execute each pending step
  for (const step of UPLOAD_STEPS) {
    // Check if we went offline during processing
    if (!isOnline) {
      await updateWorkerUploadStatus(dbId, { status: OnboardingStatus.PENDING });
      return false;
    }

    const isPending = worker.uploadingDetails?.pendingUploads?.[step.key];
    if (!isPending) continue;
    if (isPending && !areAllRequiredFieldsComplete(step.key, worker.uploadingDetails.sectionsCompleted)) {
      // If the step is pending but required fields are not complete, mark as INCOMPLETE and skip processing
      await updateWorkerUploadStatus(dbId, { status: OnboardingStatus.INCOMPLETE });
      return false;
    }

    try {
      const result = await step.handler(worker, getLanguageCode());

      // Handler returned null = API not implemented, mark as INCOMPLETE silently
      if (result === null) {
        await updateWorkerUploadStatus(dbId, { status: OnboardingStatus.INCOMPLETE });
        return false;
      }

      worker = result;
    } catch (error: any) {
      // Check if it's a network error (no response means network issue)
      const isNetworkError =
        error.code === 'ERR_NETWORK' ||
        error.code === 'ECONNABORTED' ||
        error.message === 'Network Error' ||
        error.message?.includes('timeout');

      if (isNetworkError) {
        // Network error - reset to PENDING for retry later
        await updateWorkerUploadStatus(dbId, { status: OnboardingStatus.PENDING });
        return false;
      }

      // Actual API failure - stop and mark as INCOMPLETE
      const errorObject = error?.response?.data?.errors || error?.response?.data;
      const resolvedPageUrl = WORKER_UPDATE_STEPS.has(step.key)
        ? getPageUrlFromBackendErrors(errorObject, step.pageUrl)
        : step.pageUrl;

      await updateWorkerUploadStatus(dbId, {
        status: OnboardingStatus.INCOMPLETE,
        errors: {
          pageUrl: resolvedPageUrl,
          errorObject,
        },
      });
      return false;
    }
  }

  // Check if all steps are complete using sectionsCompleted
  const isAllStepsComplete = Object.values(worker.uploadingDetails?.sectionsCompleted || {}).every(value => value);

  await updateWorkerUploadStatus(dbId, {
    status: isAllStepsComplete ? OnboardingStatus.COMPLETED : OnboardingStatus.INCOMPLETE,
    isOffline: false,
  });

  // Fire-and-forget: refresh worker data from backend without blocking completion
  if (isAllStepsComplete && worker.id) {
    refreshWorkerData(dbId, worker.id, getLanguageCode());
  }

  return true;
};

/**
 * Process the auto queue (1 at a time).
 */
const processAutoQueue = async (): Promise<void> => {
  if (!isOnline || currentAutoUpload || autoQueue.length === 0) {
    return;
  }

  const dbId = autoQueue.shift()!;

  // Skip if already being uploaded manually
  if (manualUploads.has(dbId)) {
    processAutoQueue();
    return;
  }

  currentAutoUpload = dbId;

  await executeUpload(dbId);

  currentAutoUpload = null;

  // Continue with next in queue
  processAutoQueue();
};

// ============================================================================
// Public API
// ============================================================================

/**
 * Add worker to auto-upload queue (background, 1 at a time).
 */
export const enqueueAuto = (dbId: string): void => {
  if (autoQueue.includes(dbId) || currentAutoUpload === dbId || manualUploads.has(dbId)) {
    return;
  }

  autoQueue.push(dbId);
  processAutoQueue();
};

/**
 * Manually upload a worker (separate from auto queue, max 2 concurrent).
 */
export const uploadManual = async (dbId: string): Promise<void> => {
  if (!isOnline) {
    return;
  }

  // Already uploading this worker
  if (manualUploads.has(dbId) || currentAutoUpload === dbId) {
    return;
  }

  // Check concurrent limit
  if (manualUploads.size >= MAX_MANUAL_CONCURRENT) {
    enqueueSnackbar(i18n.t('backgroundUpload.maxLimit') as string, {
      variant: 'error',
    });
    return;
  }

  // Remove from auto queue if present
  autoQueue = autoQueue.filter(id => id !== dbId);

  manualUploads.add(dbId);

  await executeUpload(dbId);

  manualUploads.delete(dbId);
};

/**
 * Reset orphaned PROCESSING workers back to PENDING.
 * Workers are considered orphaned if they are in PROCESSING state in IndexedDB
 * but NOT tracked in the current session's in-memory queues (currentAutoUpload / manualUploads).
 * This handles the case where the browser tab was closed mid-upload.
 */
const resetOrphanedProcessingWorkers = async (projectId: string): Promise<void> => {
  const processingWorkers = await getProcessingWorkersByProject(projectId);

  for (const worker of processingWorkers) {
    const dbId = worker.db?.id;
    if (!dbId) continue;

    // Skip workers actively uploading in this session
    if (currentAutoUpload === dbId || manualUploads.has(dbId)) continue;

    await updateWorkerUploadStatus(dbId, { status: OnboardingStatus.PENDING });
  }
};

/**
 * Start uploading all pending workers for a project (auto queue).
 */
export const startPendingUploads = async (projectId: string): Promise<void> => {
  if (!isOnline) {
    return;
  }

  currentProjectId = projectId;

  // Reset orphaned PROCESSING workers from prior sessions before fetching pending workers
  await resetOrphanedProcessingWorkers(projectId);

  const pendingWorkers = await getPendingWorkersByProject(projectId);

  pendingWorkers.forEach(worker => {
    if (worker.db?.id) {
      enqueueAuto(worker.db.id);
    }
  });
};

/**
 * Set online/offline status.
 */
export const setOnline = async (online: boolean): Promise<void> => {
  isOnline = online;

  if (!online) {
    await pauseAll();
  }
};

/**
 * Set current project ID.
 */
export const setProjectId = (projectId: string): void => {
  currentProjectId = projectId;
};

/**
 * Set current language code (synced from store.app.languageCode).
 */
export const setLanguageCode = (languageCode: string): void => {
  currentLanguageCode = languageCode;
};

/**
 * Pause all uploads:
 * - Reset PROCESSING → PENDING
 * - Add back to front of queue
 */
export const pauseAll = async (): Promise<void> => {
  // Collect IDs to reset
  const toReset: string[] = [];

  if (currentAutoUpload) {
    toReset.push(currentAutoUpload);
    currentAutoUpload = null;
  }

  manualUploads.forEach(dbId => {
    toReset.push(dbId);
  });
  manualUploads.clear();

  // Reset status and re-queue
  for (const dbId of toReset) {
    await updateWorkerUploadStatus(dbId, { status: OnboardingStatus.PENDING });
    if (!autoQueue.includes(dbId)) {
      autoQueue.unshift(dbId);
    }
  }
  autoQueue = [];
};

/**
 * Get current status (for debugging).
 */
export const getStatus = () => ({
  autoQueueLength: autoQueue.length,
  currentAutoUpload: currentAutoUpload || null,
  manualUploadsCount: manualUploads.size,
  manualUploadIds: Array.from(manualUploads),
  isOnline,
  currentProjectId,
});

/**
 * Clear all queues and abort everything.
 */
export const clearAll = (): void => {
  autoQueue = [];
  currentAutoUpload = null;
  manualUploads.clear();
};
